import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';
import { getJoinApplications } from '../utils/datastore';

const Referral = () => {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    const load = async () => {
      const userEmail = String(user?.email || '').trim().toLowerCase();
      const uid = String(user?._id || user?.id || '');
      const code = String(user?.referralCode || '');

      try {
        if (!uid) {
          const apps = getJoinApplications();
          const list = apps.filter(a =>
            String(a.referralCode || '') === code &&
            String(a.email || '').trim().toLowerCase() !== userEmail
          );
          setReferrals(list);
          return;
        }
        const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const res = await axios.get(`/api/users/${uid}/referrals`, { params: { limit: 100 }, headers });
        const apiRefs = res.data?.data?.referrals || [];
        const filtered = apiRefs.filter(r =>
          String(r.email || '').trim().toLowerCase() !== userEmail &&
          String(r.id || r._id || '') !== uid
        );
        setReferrals(filtered.map(r => ({
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email,
          time: r.createdAt,
          status: r.status || 'active'
        })));
      } catch (e) {
        const apps = getJoinApplications();
        const list = apps.filter(a =>
          String(a.referralCode || '') === code &&
          String(a.email || '').trim().toLowerCase() !== userEmail
        );
        setReferrals(list);
      }
    };
    load();
  }, [user?.referralCode, user?._id, user?.id, user?.email]);

  const link = user?.referralCode ? `${window.location.origin}/home?ref=${user.referralCode}` : '';

  const copyLink = () => {
    if (!link) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied to clipboard');
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
      document.body.prepend(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Referral link copied to clipboard');
      } catch (error) {
        toast.error('Failed to copy');
      } finally {
        textArea.remove();
      }
    }
  };

  return (
    <div className="min-h-screen hero-gradient mobile-padding py-6">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 sm:mb-8">
          <h1 className="mobile-header font-bold text-white mb-2">Referral</h1>
          <p className="text-gray-300 mobile-text">Share your link and track your referrals</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mobile-glass rounded-xl mobile-card mb-6">
          <h3 className="text-lg font-bold text-white mb-3">Your Referral Link</h3>
          {user?.referralCode ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-xl p-3 sm:px-4">
              <a href={link} target="_blank" rel="noopener noreferrer" className="underline text-blue-300 hover:text-blue-200 break-all text-xs sm:text-sm">
                {link}
              </a>
              <button onClick={copyLink} className="self-end sm:self-center shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 border border-blue-400/30 text-white text-xs font-semibold transition-all">
                <Copy className="w-3.5 h-3.5" />
                <span>Copy</span>
              </button>
            </div>
          ) : (
            <div className="text-gray-200">No referral code available</div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-400" />
            Your Referrals
          </h3>
          {referrals.length === 0 ? (
            <div className="text-gray-300">No referrals yet</div>
          ) : (
            <div className="space-y-3">
              {referrals.map((r, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm sm:text-base truncate">{`${r.firstName || ''} ${r.lastName || ''}`.trim() || (r.email || 'user')}</p>
                    <p className="text-xs text-gray-400 truncate">{r.email}</p>
                  </div>
                  <div className="text-left sm:text-right pt-2 sm:pt-0 border-t sm:border-t-0 border-white/10">
                    <p className="text-xs text-gray-300">{new Date(r.time || Date.now()).toLocaleString()}</p>
                    <p className="text-xs text-blue-400 font-bold uppercase tracking-wider">{r.status || 'pending'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Referral;
